import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Walk-in → booking: pick flat(s), form number, date, amount and booked/hold.
// Creates the sale(s), links client + broker + walk-in and marks the flat(s).
const flatLabel = (u) => `${String(u.tower_name || '').replace(/^Wing\s*/i, '')}-${u.floor_number}${String(u.unit_number).padStart(2, '0')}`;

const BookUnitModal = ({ apiBase, walkin, onClose, onBooked }) => {
  const [units, setUnits] = useState([]);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({
    form_number: '', booking_date: new Date().toISOString().slice(0, 10),
    booking_amount: '', status: 'booked', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const qs = walkin.project_id ? `?project_id=${walkin.project_id}` : '';
    axios.get(`${apiBase}/booking-flow/units${qs}`).then((r) => setUnits(r.data || [])).catch(() => setError('Could not load flats.'));
  }, [apiBase, walkin.project_id]);

  const free = (u) => !u.client_id || u.client_id === walkin.client_id;
  const term = q.trim().toLowerCase().replace(/\s|wing/g, '');
  const list = units.filter((u) => !picked.includes(u.id) && free(u) &&
    (!term || flatLabel(u).toLowerCase().replace('-', '').includes(term.replace('-', '')) || String(u.bhk_type || '').toLowerCase().includes(term)))
    .slice(0, 40);
  const pickedUnits = units.filter((u) => picked.includes(u.id));

  const save = async (e) => {
    e.preventDefault();
    if (!picked.length) { setError('Pick at least one flat.'); return; }
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${apiBase}/booking-flow/book`, { ...form, walkin_id: walkin.id, unit_ids: picked });
      onBooked(r.data);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Your role is not allowed to record bookings.' : (err.response?.data?.error || 'Could not record the booking.'));
    } finally { setSaving(false); }
  };

  const field = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '4px' };
  const cell = { flex: '1 1 200px', minWidth: 0 };

  return (
    <div onClick={() => !saving && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '640px', padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Book unit</h3>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280' }} aria-label="Close">×</button>
        </div>
        <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: '16px' }}>
          {walkin.name || 'Client'} · {walkin.broker_name ? `via ${walkin.broker_name}${walkin.broker_company ? ` (${walkin.broker_company})` : ''}` : 'Direct'}
        </div>

        <label style={lbl}>Flat(s)</label>
        {pickedUnits.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {pickedUnits.map((u) => (
              <span key={u.id} style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: '999px', padding: '4px 10px', fontSize: '13px' }}>
                {flatLabel(u)} · {u.bhk_type} · {u.carpet_area} sq ft
                <button type="button" onClick={() => setPicked(picked.filter((x) => x !== u.id))} style={{ border: 'none', background: 'none', marginLeft: '6px', cursor: 'pointer', color: '#1d4ed8' }}>×</button>
              </span>
            ))}
          </div>
        )}
        <input style={field} placeholder="Search flat, e.g. A-1907 or 2BHK" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '6px' }}>
          {list.map((u) => (
            <div key={u.id} onClick={() => { setPicked([...picked, u.id]); setQ(''); }}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 10px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
              <span><b>{flatLabel(u)}</b> · {u.bhk_type} · {u.carpet_area} sq ft</span>
              <span style={{ color: /hold|pending/i.test(u.status) ? '#b45309' : '#059669' }}>{u.status}</span>
            </div>
          ))}
          {!list.length && <div style={{ padding: '10px', fontSize: '13px', color: '#9ca3af' }}>No free flats match.</div>}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '14px' }}>
          <div style={cell}><label style={lbl}>Booking form no.</label><input style={field} value={form.form_number} onChange={(e) => setForm({ ...form, form_number: e.target.value })} placeholder="e.g. K00025" /></div>
          <div style={cell}><label style={lbl}>Booking date</label><input style={field} type="date" value={form.booking_date} onChange={(e) => setForm({ ...form, booking_date: e.target.value })} /></div>
          <div style={cell}><label style={lbl}>Booking amount (₹)</label><input style={field} type="number" min="0" value={form.booking_amount} onChange={(e) => setForm({ ...form, booking_amount: e.target.value })} /></div>
          <div style={cell}>
            <label style={lbl}>Status</label>
            <select style={field} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="booked">Booked (cheque received)</option>
              <option value="hold">Hold (no cheque yet)</option>
            </select>
          </div>
          <div style={{ flex: '1 1 100%' }}><label style={lbl}>Notes</label><textarea style={{ ...field, minHeight: '60px' }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>

        {error && <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', color: '#b91c1c', borderRadius: '6px', fontSize: '13px' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Record booking'}</button>
        </div>
      </form>
    </div>
  );
};

export default BookUnitModal;
